import { Input, Select } from 'antd';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';

import phonePrefixes from '@src/packages/utils/hooks/useRulesByCountry/phone/phonePrefixes.json';
import {
  findRuleByCountryCode,
  normalizePhoneInput,
} from '@src/packages/utils/hooks/useRulesByCountry/phone/phonePrefixValidation';

const { Option } = Select;

const DEFAULT_PREFIX = '+1';

// Pre-build Option elements once — phonePrefixes is static
const PREFIX_OPTIONS = phonePrefixes.map(p => (
  <Option key={p.code} value={p.dial_code} label={`${p.flag} ${p.dial_code}`}>
    {`${p.flag} ${p.dial_code} ${p.name}`}
  </Option>
));

/**
 * Finds the longest matching dial code prefix in a phone string.
 * Sorted descending by length so "+598" matches before "+5".
 */
const parsePrefixFromValue = (value, countryCode = undefined) => {
  const fixedRule = findRuleByCountryCode(countryCode);
  if (fixedRule) return fixedRule.dial_code;

  if (!value) return DEFAULT_PREFIX;
  const sorted = [...phonePrefixes].sort((a, b) => b.dial_code.length - a.dial_code.length);
  const match = sorted.find(p => value.startsWith(p.dial_code));
  return match ? match.dial_code : DEFAULT_PREFIX;
};

const splitValue = (value, countryCode = undefined) => {
  const prefix = parsePrefixFromValue(value, countryCode);
  const normalized = normalizePhoneInput(value);

  if (!normalized) return { prefix, number: '' };
  if (normalized.startsWith(prefix)) {
    return { prefix, number: normalized.slice(prefix.length) };
  }

  const sorted = [...phonePrefixes].sort((a, b) => b.dial_code.length - a.dial_code.length);
  const match = sorted.find(p => normalized.startsWith(p.dial_code));
  if (match) {
    return { prefix, number: normalized.slice(match.dial_code.length) };
  }

  return { prefix, number: normalized.replace(/^\+\d*/, '') || normalized };
};

/**
 * A controlled/uncontrolled phone number input that behaves like a native antd field.
 *
 * - `value` / `defaultValue`: the full phone string (e.g. "+56912345678")
 * - `onChange(fullValue)`: called with the concatenated prefix + local number
 */
const PhoneInput = ({
  value,
  defaultValue = undefined,
  onChange = undefined,
  disabled = false,
  countryCode = undefined,
  placeholder = undefined,
  size = undefined,
  className = undefined,
  style = undefined,
  ...restProps
}) => {
  const isControlled = value !== undefined;
  const lockedPrefixRule = useMemo(() => findRuleByCountryCode(countryCode), [countryCode]);

  const [internalValue, setInternalValue] = useState(() => defaultValue || '');
  const effectiveValue = isControlled ? value : internalValue;

  const { prefix, number } = useMemo(
    () => splitValue(effectiveValue, countryCode),
    [effectiveValue, countryCode],
  );

  const triggerChange = useCallback(
    newFull => {
      if (!isControlled) setInternalValue(newFull);
      onChange?.(newFull);
    },
    [isControlled, onChange],
  );

  useEffect(() => {
    if (!lockedPrefixRule) return;
    const normalized = normalizePhoneInput(effectiveValue);
    if (!normalized || normalized.startsWith(lockedPrefixRule.dial_code)) return;
    const { number: nationalNumber } = splitValue(normalized, countryCode);
    const nextValue = `${lockedPrefixRule.dial_code}${nationalNumber}`;
    if (nextValue !== normalized) {
      triggerChange(nextValue);
    }
  }, [countryCode, effectiveValue, lockedPrefixRule, triggerChange]);

  const handlePrefixChange = useCallback(
    newPrefix => {
      triggerChange(`${newPrefix}${number}`);
    },
    [triggerChange, number],
  );

  const handleNumberChange = useCallback(
    e => {
      triggerChange(`${prefix}${e.target.value}`);
    },
    [triggerChange, prefix],
  );

  const prefixOptions = lockedPrefixRule ? (
    <Option
      key={lockedPrefixRule.code}
      value={lockedPrefixRule.dial_code}
      label={`${lockedPrefixRule.flag} ${lockedPrefixRule.dial_code}`}
    >
      {`${lockedPrefixRule.flag} ${lockedPrefixRule.dial_code} ${lockedPrefixRule.name}`}
    </Option>
  ) : (
    PREFIX_OPTIONS
  );

  const selectBefore = (
    <Select
      value={prefix}
      onChange={handlePrefixChange}
      disabled={disabled || Boolean(lockedPrefixRule)}
      showSearch={!lockedPrefixRule}
      optionLabelProp="label"
      optionFilterProp="children"
      dropdownMatchSelectWidth={false}
      size={size}
      style={{ width: 100 }}
      data-testid="phone-prefix-select"
    >
      {prefixOptions}
    </Select>
  );

  return (
    <Input
      addonBefore={selectBefore}
      value={number}
      onChange={handleNumberChange}
      disabled={disabled}
      placeholder={placeholder}
      size={size}
      className={className}
      style={style}
      data-testid="phone-number-input"
      {...restProps}
    />
  );
};

PhoneInput.propTypes = {
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  /** ISO 3166-1 alpha-2 code (e.g. CL, AR). Locks the dial prefix to that country. */
  countryCode: PropTypes.string,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(['small', 'middle', 'large']),
  className: PropTypes.string,
  style: PropTypes.object,
};

export default PhoneInput;
